import { checkLocalStorageAvailable, checkSessionStorageAvailable, uid } from 'tools/tools';
import { IMenuSection } from 'app/app/editor/tabs/components';
import { CSSProperties } from 'react';
import { BroadcastMessageType } from 'app/app/editor/broadcast/useAppEditorChannel';
import { IHistory } from 'app/app/editor/useHistory';
import { IComponentNodeDeps } from 'app/app/editor/tabs/settings/deps';

export type ComponentNodeType = 'container' | 'component';
export type ComponentNodeSubType = 'none' | 'page_menu';
export interface IComponentNode {
    elements?: Array<IComponentNode>;
    params: {
        uid: string;
        layout?: any; //CSSProperties
        attr?: {
            title?: string;
            deps?: Array<IComponentNodeDeps>;
            visibility?: 'const' | 'cond';
        };
        styles?: CSSProperties;
    };
    type: ComponentNodeType;
    subtype?: ComponentNodeSubType;
}

export type ComponentListType = {
    [key in string]: {
        // TODO: replace with Component Interface
        component: string;
        settings: any;
        variables?: any;
    };
};

export interface ICssVariables {
    title: string;
    name: string;
    type: 'color' | 'font';
    value: string;
}

export interface IPage {
    components: ComponentListType;
    layout: IComponentNode;
    screen: { [key in 'phone' | 'tablet']: IComponentNode };
    cssVariables: ICssVariables[];
}

export interface ICopyBuffer {
    node: IComponentNode | null;
    cut: boolean;
}

export type AppBuilderContextType = {
    setComponentUid(uid: string | null): void;
    setComponentQuerySelector(querySelector: string | null): void;
    sendMessage(msg: BroadcastMessageType): any;
    editComponent(data: any): void;
    storageKey: string;
    withTemplate: boolean;
    componentQuerySelector: string | null;
    page: IPage;
    setPage(page: Partial<IPage>, mergeObject?: boolean): void;
    componentsMenu: Array<IMenuSection>;
    state: any;
    history: IHistory;
    copyBuffer: ICopyBuffer;
    setCopyBuffer: (buffer: ICopyBuffer) => void;
    isTemplate: boolean;
    pageInternalName: string;
    pageSession: string;
};

export const getPageFromStorage = function (pageKey: string): any | null {
    let page: any = null;

    if (checkLocalStorageAvailable())
        try {
            let page = localStorage.getItem(pageKey);
            if (null !== page) {
                return JSON.parse(page);
            }
        } catch (e: any) {}

    if (checkSessionStorageAvailable())
        try {
            let page = sessionStorage.getItem(pageKey);
            if (null !== page) {
                return JSON.parse(page);
            }
        } catch (e: any) {}

    return null;
};

export const setPageToStorage = function (pageKey: string, page: any): void {
    let isCached = false;
    let pageString = JSON.stringify(page);

    if (checkLocalStorageAvailable()) {
        try {
            localStorage.setItem(pageKey, pageString);
            isCached = true;
        } catch (e: any) {
            if (
                ('undefined' != typeof DOMException.QUOTA_EXCEEDED_ERR && e == DOMException.QUOTA_EXCEEDED_ERR) ||
                e.name == 'QuotaExceededError' ||
                e.name == 'NS_ERROR_DOM_QUOTA_REACHED'
            ) {
                localStorage.clear();

                try {
                    localStorage.setItem(pageKey, pageString);
                    isCached = true;
                } catch (e: any) {}
            }
        }
    }

    if (!isCached && checkSessionStorageAvailable()) {
        try {
            sessionStorage.setItem(pageKey, pageString);
        } catch (e: any) {
            if (
                ('undefined' != typeof DOMException.QUOTA_EXCEEDED_ERR && e == DOMException.QUOTA_EXCEEDED_ERR) ||
                e.name == 'QuotaExceededError' ||
                e.name == 'NS_ERROR_DOM_QUOTA_REACHED'
            ) {
                sessionStorage.clear();

                try {
                    sessionStorage.setItem(pageKey, pageString);
                } catch (e: any) {}
            }
        }
    }

    window.parent.postMessage({ name: 'jsonModified', data: pageString }, '*');
};

export const processSettingsDefaults = (component: any, formJson: any): object => {
    let result;
    if (component?.settings && !['{}', '[]'].includes(JSON.stringify(component?.settings))) {
        let applyVariables = (settings: any, variables: any) => {
            for (const key in settings)
                if ('object' === typeof settings[key]) {
                    if (
                        'string' === typeof settings[key]['variableType'] &&
                        'variable' == settings[key]['variableType'] &&
                        'string' === typeof settings[key]['guid']
                    ) {
                        for (let i = 0; i < variables.length; i++)
                            if (variables[i]['guid'] == settings[key]['guid']) {
                                settings[key] = variables[i].value;
                                break;
                            }
                    } else applyVariables(settings[key], variables);
                }
        };

        let settings = JSON.parse(JSON.stringify(component.settings));
        if (component?.variables && component.variables.length > 0) applyVariables(settings, component.variables);

        result = settings;
    } else {
        let defaults: { [key: string]: any } = {};
        if (formJson?.data?.fields)
            for (let key in formJson.data.fields) {
                let field = formJson.data.fields[key];
                if (field.default) defaults[key] = field.default;
            }

        result = defaults;
    }
    for (const [k, v] of Object.entries(result)) {
        if (typeof v == 'object') {
            result[k] = JSON.stringify(v);
        }
    }
    return result;
};

export const processSettingsVariables = function (fields: any, formData: any): any {
    let variables: any[] = [];
    let settings = formData;

    if (fields)
        for (let key in fields) {
            let field = fields[key];
            if (field.variable) {
                let guid = 'variable_' + field.variable + '_' + uid();
                variables.push({ name: key, guid, value: formData[key], type: field.item_type ?? '' });
                settings[key] = {
                    variableType: 'variable',
                    guid: guid,
                };
            }
        }

    return { settings: settings, variables: variables };
};

export const findContainer = function (node: any, componentUid: string, isParent: boolean): any {
    if (!node) return null;
    if (!isParent && node.params.uid == componentUid) return node;
    if (node.elements) {
        if (isParent && node.elements[0].params.uid == componentUid) return node;

        for (let i = 0; i < node.elements.length; i++) {
            let r = findContainer(node.elements[i], componentUid, isParent);
            if (null !== r) return r;
        }
    }
    return null;
};
